import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";

/**
 * Helpers for FY-based period ranges (Apr 1 – Mar 31, India).
 */
export function fyOptions(count = 5) {
  const today = new Date();
  const curFyStartYear = today.getMonth() + 1 >= 4 ? today.getFullYear() : today.getFullYear() - 1;
  const arr = [];
  for (let i = 0; i < count; i++) {
    const y = curFyStartYear - i;
    arr.push({ value: `FY${y}`, label: `FY ${y}-${String(y + 1).slice(2)}` });
  }
  return arr;
}

export function periodToDates(period) {
  // period values: "this_month" | "last_month" | "this_quarter" | "this_year" | "FY<year>" | "" | "custom"
  const t = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  if (!period || period === "all" || period === "custom") return { start_date: "", end_date: "" };
  if (period === "this_month") {
    const s = new Date(t.getFullYear(), t.getMonth(), 1);
    const e = new Date(t.getFullYear(), t.getMonth() + 1, 0);
    return { start_date: iso(s), end_date: iso(e) };
  }
  if (period === "last_month") {
    const s = new Date(t.getFullYear(), t.getMonth() - 1, 1);
    const e = new Date(t.getFullYear(), t.getMonth(), 0);
    return { start_date: iso(s), end_date: iso(e) };
  }
  if (period === "this_quarter") {
    const q = Math.floor(t.getMonth() / 3);
    const s = new Date(t.getFullYear(), q * 3, 1);
    const e = new Date(t.getFullYear(), q * 3 + 3, 0);
    return { start_date: iso(s), end_date: iso(e) };
  }
  if (period === "this_year") {
    const s = new Date(t.getFullYear(), 0, 1);
    const e = new Date(t.getFullYear(), 11, 31);
    return { start_date: iso(s), end_date: iso(e) };
  }
  if (period.startsWith("FY")) {
    const y = parseInt(period.slice(2), 10);
    return { start_date: `${y}-04-01`, end_date: `${y + 1}-03-31` };
  }
  return { start_date: "", end_date: "" };
}

/**
 * Compact period selector. Emits dates via onChange({start_date, end_date, period}).
 * Pass `value` = period, `start`/`end` for custom dates.
 */
export default function PeriodFilter({ value = "", start = "", end = "", onChange, testId = "period-filter" }) {
  const fys = fyOptions(5);
  const setPeriod = (p) => {
    if (p === "custom") {
      onChange({ period: p, start_date: start || "", end_date: end || "" });
    } else {
      const d = periodToDates(p);
      onChange({ period: p, ...d });
    }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Select value={value || "all"} onValueChange={(v) => setPeriod(v === "all" ? "" : v)}>
        <SelectTrigger className="rounded-sm w-44 h-9" data-testid={`${testId}-period`}><SelectValue placeholder="Period"/></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Time</SelectItem>
          <SelectItem value="this_month">This Month</SelectItem>
          <SelectItem value="last_month">Last Month</SelectItem>
          <SelectItem value="this_quarter">This Quarter</SelectItem>
          <SelectItem value="this_year">This Calendar Year</SelectItem>
          {fys.map((fy) => <SelectItem key={fy.value} value={fy.value}>{fy.label}</SelectItem>)}
          <SelectItem value="custom">Custom range...</SelectItem>
        </SelectContent>
      </Select>
      {value === "custom" && (
        <>
          <Input
            type="date" value={start} onChange={(e) => onChange({ period: "custom", start_date: e.target.value, end_date: end })}
            className="rounded-sm w-36 h-9" data-testid={`${testId}-start`}
          />
          <span className="text-xs text-slate-400">to</span>
          <Input
            type="date" value={end} onChange={(e) => onChange({ period: "custom", start_date: start, end_date: e.target.value })}
            className="rounded-sm w-36 h-9" data-testid={`${testId}-end`}
          />
        </>
      )}
    </div>
  );
}
